import { handleInputSchema, requestClientHandle } from '../shared/api-client/client-handle';
import { currentClientSettingsAccount as account } from './clientSettingsAccount';
const empty = () => ({ confirmed: null, edits: {}, busy: null, needsReload: false, message: '', tone: 'info' });
/** Browser drafts belong to one mounted account visit; only checked receipts confirm a save. */
export class ClientHandleSettingsController {
    constructor(onChange) {
        this.onChange = onChange;
        this.owner = null;
        this.state = empty();
        this.active = false;
        this.generation = 0;
        this.pending = null;
    }
    setActive(active) {
        const owner = active ? account() : null;
        if (active === this.active && owner?.key === this.owner?.key)
            return;
        this.pending?.abort();
        this.pending = null;
        this.generation++;
        this.active = active;
        this.owner = owner;
        this.state = empty();
        if (!active)
            return;
        if (owner)
            void this.run();
        else
            this.publish({ message: 'Sign in again to load your handle.', tone: 'error' });
    }
    current() { return this.active && Boolean(this.owner) && this.owner?.key === account()?.key; }
    publish(patch) {
        this.state = { ...this.state, ...patch };
        if (this.active)
            this.onChange(this.state);
    }
    snapshot() { return this.state; }
    /** Obsolete DOM callbacks cannot act during a later view/account visit. */
    actions() {
        const generation = this.generation;
        const current = () => generation === this.generation && this.current();
        return {
            edit: (patch) => {
                if (current())
                    this.publish({ edits: { ...this.state.edits, ...patch }, message: 'Changes not saved.', tone: 'info' });
            },
            reload: () => { if (current())
                void this.run(); },
            save: () => {
                if (!current() || this.pending || !this.state.confirmed || this.state.needsReload)
                    return;
                const parsed = handleInputSchema.safeParse({ ...this.state.confirmed, ...this.state.edits });
                if (!parsed.success) {
                    this.publish({ message: 'Use 3–32 letters, numbers, dots, dashes or underscores.', tone: 'error' });
                    return;
                }
                void this.run(parsed.data);
            }
        };
    }
    async run(input) {
        const owner = this.owner;
        if (!owner || !this.current() || this.pending)
            return;
        const work = new AbortController();
        this.pending = work;
        const current = () => this.pending === work && this.current();
        this.publish({ busy: input ? 'save' : 'load', needsReload: Boolean(input) || this.state.needsReload,
            message: input ? 'Saving handle…' : 'Loading saved handle…', tone: 'info' });
        const result = await requestClientHandle(owner, work.signal, current, input);
        if (!current()) {
            if (this.pending === work) {
                this.pending = null;
                this.setActive(this.active);
            }
            return;
        }
        this.pending = null;
        if (result.error) {
            const message = result.error === 'auth' ? 'Sign in again to continue. Your entries are kept.'
                : result.error === 'taken' ? 'That handle is taken. Choose another and reload saved details before saving.'
                    : input ? 'Handle save not confirmed. Your entries are kept. Reload saved details before saving again.'
                        : 'Saved handle unavailable. Your entries are kept. Retry when ready.';
            this.publish({ busy: null, message, tone: 'error' });
            return;
        }
        const edits = { ...this.state.edits };
        if (input) {
            const parsed = handleInputSchema.safeParse({ ...input, handle: edits.handle ?? input.handle });
            if (parsed.success && parsed.data.handle === input.handle)
                delete edits.handle;
            if (edits.visibility === input.visibility)
                delete edits.visibility;
        }
        this.publish({ confirmed: result.data, edits, busy: null, needsReload: false,
            message: Object.keys(edits).length ? 'Your entries still need saving.' : input ? 'Handle saved.' : '',
            tone: input && !Object.keys(edits).length ? 'success' : 'info' });
    }
}
